import { Box, Button, Stack } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { BackButton } from '~/src/components/atoms/Button/BackButton/BackButton'
import { ProtectedPage } from '~/src/components/templates'
import { NegotiationDetails, MessageList, LoadingNormal } from '~/src/components/organisms'
import { orderRepository } from '~/src/repositories'
import { useMessage } from '~/src/hooks'

import { useContactOrderHooks } from '../hooks/useContactOrderHooks'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'

export const ContactOrderDetail: React.VFC = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { handleOpenWithErrorMessage, handleOpenWithSuccessMessage } = useMessage()
  const { id } = useParams()
  const [isLoading, setLoading] = useState(false)

  const { data, mutate, error } = useSWR([`/request-orders/${id}`], () =>
    orderRepository.requestOrderDetailExpand(id as string)
  )

  const { contactOrderListMutate, setBrowserBackStateAtom } = useContactOrderHooks(undefined)

  useEffect(() => {
    if (error) handleOpenWithErrorMessage(error.message)
  }, [error])

  /**
   * onSubmitNegotiationDetail
   * @param values
   */
  const onSubmitNegotiationDetail = async (values: {
    refNo: string
    partsCode: string
    manufacturer: string
    carName: string
    vehicleModel: string
    engineModel: string
    drive1: string
    transmission: string
    drive2: string
    year: number
    orderDetail: string
    finalPrice: string
    cancelReason: string
    supplyType: string
    orderDate: string
    arrivalDate: string
    supplier: string
    remark: string
    status: number
    genuinePartNumber: string
    originalRemark: string
    type: string
    engineDetail: string
    withCatalyst: boolean
  }) => {
    setLoading(true)
    try {
      await orderRepository.patchRequestOrder(id as string, values)
      mutate(null)
      await contactOrderListMutate()
      setBrowserBackStateAtom({ page: 'contactOrder', text: new Date().toLocaleString() })
      handleOpenWithSuccessMessage('更新しました')
    } catch (e: any) {
      console.error(e)
      handleOpenWithErrorMessage(e.message)
    } finally {
      setLoading(false)
    }
  }

  const onClickPrevious = () => {
    navigate(`/admin/order/contact-order/detail/${data.requestOrderExpand.previousRequestOrderId}`, {
      replace: true,
      state: {
        prevLocationState: { ...location.state.prevLocationState}
      }
    })
  }

  const onClickNext = () => {
    navigate(`/admin/order/contact-order/detail/${data.requestOrderExpand.nextRequestOrderId}`, {
      replace: true,
      state: {
        prevLocationState: { ...location.state.prevLocationState}
      }
    })
  }

  if (!data) return <LoadingNormal />

  return (
    <ProtectedPage pageTitle={t('contact_order_detail.title')}>
      <Stack mb={'275px'}>
        <Box display={'flex'} justifyContent={'space-between'}>
          <BackButton location={location} />
          <Box>
            <Button
              color="primary"
              startIcon={<ArrowBackIos />}
              variant="outlined"
              size={'medium'}
              sx={{
                bgcolor:'#FFFFFF',
                marginRight: '8px'
              }}
              disabled={!data.requestOrderExpand.previousRequestOrderId}
              onClick={onClickPrevious}
            >
              {t('contact_order_detail.previous_order')}
            </Button>
            <Button
              color="primary"
              endIcon={<ArrowForwardIos />}
              variant="outlined"
              size={'medium'}
              sx={{
                bgcolor:'#FFFFFF'
              }}
              disabled={!data.requestOrderExpand.nextRequestOrderId}
              onClick={onClickNext}
            >
              {t('contact_order_detail.next_order')}
            </Button>
          </Box>
        </Box>

        <NegotiationDetails onSubmit={onSubmitNegotiationDetail} data={data.requestOrder} loading={isLoading} />
        <Box mt={'20px'}>
          <MessageList id={id as string} />
        </Box>
      </Stack>
    </ProtectedPage>
  )
}
